import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Car, 
  Calendar, 
  TrendingUp, 
  FileText,
  Heart,
  UserPlus,
  Mail,
  Download,
  CreditCard,
  DollarSign,
  Bell
} from 'lucide-react';
import { supabase, db as supabaseAnalytics } from '../../lib/supabase';
import { db as dashboardDb } from '../../lib/database';
import { useAuth } from '../../hooks/useAuth';
import {
  useAdminBookingsRealtime,
  requestAdminBookingNotifications,
} from '../../hooks/useAdminBookingsRealtime';
import { formatCurrency } from '../../lib/stripe';
import { scrollToActionZone } from '../../utils/scrollUtils';
import PaymentAnalytics from '../../components/PaymentAnalytics';
import PerformanceMonitor from '../../components/PerformanceMonitor';

type DriverIntakeBrief = {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  created_at: string | null;
  status: string;
  registration_plate: string;
  vehicle_make: string;
  vehicle_model: string;
};

type SwIntakeBrief = {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  created_at: string | null;
  status: string;
  experience_summary: string;
  has_current_dbs: boolean;
};

const AdminDashboard: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const { rows: liveBookings, loading: liveLoading, error: liveError } =
    useAdminBookingsRealtime(Boolean(user && isAdmin));
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [driverIntakes, setDriverIntakes] = useState<DriverIntakeBrief[]>([]);
  const [supportIntakes, setSupportIntakes] = useState<SwIntakeBrief[]>([]);
  const [intakesLoading, setIntakesLoading] = useState(false);
  const [intakesLoadError, setIntakesLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newsletterStats, setNewsletterStats] = useState({ total: 0, active: 0 });
  const [paymentStats, setPaymentStats] = useState({
    total_revenue: 0,
    platform_fees: 0,
    driver_payouts: 0,
    support_worker_payouts: 0,
    pending_payouts: 0
  });

  const loadPartnerIntakes = async () => {
    if (!user || !isAdmin) return;
    setIntakesLoading(true);
    setIntakesLoadError(null);
    try {
      const [dRes, sRes] = await Promise.all([
        supabase
          .from('driver_intake_submissions')
          .select(
            'id,full_name,email,phone,created_at,status,registration_plate,vehicle_make,vehicle_model',
          )
          .order('created_at', { ascending: false })
          .limit(25),
        supabase
          .from('support_worker_intake_submissions')
          .select(
            'id,full_name,email,phone,created_at,status,experience_summary,has_current_dbs',
          )
          .order('created_at', { ascending: false })
          .limit(25),
      ]);
      if (dRes.error) throw dRes.error;
      if (sRes.error) throw sRes.error;
      setDriverIntakes((dRes.data ?? []) as DriverIntakeBrief[]);
      setSupportIntakes((sRes.data ?? []) as SwIntakeBrief[]);
    } catch (e) {
      setIntakesLoadError(
        e instanceof Error ? e.message : 'Could not load partner intakes',
      );
    } finally {
      setIntakesLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'Admin Dashboard - AbleGo';
    
    // Load dashboard data if user is authenticated
    if (user) {
      console.log('Admin dashboard loading for user:', user.email);
      loadDashboardData();
      loadNewsletterStats();
      loadPaymentStats();
    }
  }, [user]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    loadPartnerIntakes();
    const ch = supabase
      .channel('admin-partner-intakes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'driver_intake_submissions' },
        () => loadPartnerIntakes(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'support_worker_intake_submissions' },
        () => loadPartnerIntakes(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [user, isAdmin]);

  const fallbackDashboard = {
    total_users: 0,
    total_riders: 0,
    total_drivers: 0,
    total_support_workers: 0,
    total_bookings: 0,
    active_trips: 0,
    pending_verifications: 0,
    monthly_revenue: 0,
    recent_activity: [] as unknown[],
    new_bookings: 0,
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: overview, error } = await dashboardDb.getDashboardOverview();

      if (error) {
        const msg =
          error instanceof Error ? error.message : 'Failed to load overview';
        setError(msg);
        setDashboardData(fallbackDashboard);
        return;
      }

      if (!overview) {
        setDashboardData(fallbackDashboard);
        return;
      }

      const total_users =
        (overview.total_riders || 0) +
        (overview.total_drivers || 0) +
        (overview.total_support_workers || 0);

      setDashboardData({
        total_users,
        total_riders: overview.total_riders ?? 0,
        total_drivers: overview.total_drivers ?? 0,
        total_support_workers: overview.total_support_workers ?? 0,
        total_bookings: overview.total_bookings ?? 0,
        new_bookings: overview.new_bookings ?? 0,
        active_trips: overview.active_trips ?? 0,
        pending_verifications: overview.pending_applications ?? 0,
        monthly_revenue: overview.month_revenue ?? 0,
        recent_activity: [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setDashboardData(fallbackDashboard);
    } finally {
      setLoading(false);
    }
  };

  const loadNewsletterStats = async () => {
    try {
      // Use secure Edge Function for newsletter stats
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-analytics/newsletter-stats`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewsletterStats({
          total: data.total || 0,
          active: data.active || 0
        });
      }
    } catch (error) {
      console.error('Error loading newsletter stats:', error);
    }
  };

  const loadPaymentStats = async () => {
    try {
      // Use existing revenue analytics function for security
      const { data: revenueData, error } =
        await supabaseAnalytics.getRevenueAnalytics(30);

      if (!error && revenueData) {
        // Calculate stats from revenue analytics data
        const stats = revenueData.reduce((acc: any, day: any) => ({
          total_revenue: acc.total_revenue + (day.total_revenue || 0),
          platform_fees: acc.platform_fees + (day.platform_fees || 0),
          driver_payouts: acc.driver_payouts + (day.driver_payouts || 0),
          support_worker_payouts: acc.support_worker_payouts + (day.support_worker_payouts || 0),
          pending_payouts: acc.pending_payouts + (day.pending_payouts || 0)
        }), {
          total_revenue: 0,
          platform_fees: 0,
          driver_payouts: 0,
          support_worker_payouts: 0,
          pending_payouts: 0
        });

        setPaymentStats(stats);
      }
    } catch (error) {
      console.error('Error loading payment stats:', error);
    }
  };

  const exportNewsletterSubscribers = async () => {
    try {
      // Use secure Edge Function for export
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-analytics/newsletter-export`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error('Export failed:', response.statusText);
        return;
      }

      const data = await response.json();
      
      if (!data || data.length === 0) {
        console.log('No subscribers to export');
        return;
      }

      // Create CSV content
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map((row: any) => 
          headers.map(header => `"${row[header as keyof typeof row] || ''}"`).join(',')
        )
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting newsletter subscribers:', error);
    }
  };
  const quickStats = [
    {
      title: 'Total Users',
      value: loading ? '...' : (dashboardData?.total_users || 0),
      icon: Users,
      color: 'blue',
      change: '+12%'
    },
    {
      title: 'Platform Revenue',
      value: loading ? '£...' : formatCurrency(paymentStats.platform_fees),
      icon: DollarSign,
      color: 'green',
      change: '+23%'
    },
    {
      title: 'Monthly Revenue',
      value: loading
        ? '£...'
        : formatCurrency(dashboardData?.monthly_revenue ?? 0),
      icon: TrendingUp,
      color: 'purple',
      change: '+18%'
    },
    {
      title: 'Active Trips',
      value: loading ? '...' : (dashboardData?.active_trips || 0),
      icon: Car,
      color: 'orange',
      change: '+5%'
    }
  ];

  const adminActions = [
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: Users,
      href: '/admin/users',
      color: 'blue'
    },
    {
      title: 'Applications',
      description: 'Partner intakes (driver & support worker) — video validation queue',
      icon: FileText,
      href: '/admin/applications',
      color: 'teal'
    },
    {
      title: 'Bookings',
      description: 'Monitor and manage all bookings',
      icon: Calendar,
      href: '/admin/bookings',
      color: 'green'
    },
    {
      title: 'Vehicles',
      description: 'Verify and manage vehicle registrations',
      icon: Car,
      href: '/admin/vehicles',
      color: 'purple'
    },
    {
      title: 'Support Workers',
      description: 'Manage support worker verifications',
      icon: Heart,
      href: '/admin/support-workers',
      color: 'pink'
    },
    {
      title: 'Payment Analytics',
      description: 'View revenue, payouts, and financial analytics',
      icon: CreditCard,
      href: '/admin/payments',
      color: 'indigo'
    },
    {
      title: 'Newsletter Subscribers',
      description: 'Manage newsletter subscriptions and export lists',
      icon: Mail,
      href: '/admin/newsletter',
      color: 'orange'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600',
      teal: 'bg-teal-100 text-teal-600',
      pink: 'bg-pink-100 text-pink-600',
      indigo: 'bg-indigo-100 text-indigo-600'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <>
      <div className="space-y-6">
        {/* Quick Stats */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClasses(stat.color)}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className={`text-sm font-medium ${
                    stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                <p className="text-gray-600 text-sm">{stat.title}</p>
                {(stat.title === 'Newsletter Subscribers' || stat.title === 'Platform Revenue') && (
                  <button
                    onClick={stat.title === 'Newsletter Subscribers' ? exportNewsletterSubscribers : () => {}}
                    className={`mt-2 text-xs font-medium flex items-center ${
                      stat.title === 'Newsletter Subscribers' ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'
                    }`}
                  >
                    <Download className="w-3 h-3 mr-1" />
                    {stat.title === 'Newsletter Subscribers' ? 'Export CSV' : 'View Details'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Partner intakes — pre-account registrations (video validation) */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex flex-row items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Car className="w-5 h-5 text-blue-600" />
                  Driver intakes
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Expressions of interest from the public driver form. Arrange a video call to validate.
                </p>
              </div>
              <Link
                to="/admin/applications"
                className="text-sm font-medium text-blue-600 hover:text-blue-800 shrink-0"
              >
                Full list →
              </Link>
            </div>
            {intakesLoadError && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                {intakesLoadError}
              </p>
            )}
            {intakesLoading && driverIntakes.length === 0 ? (
              <div className="text-center py-8 text-gray-600 text-sm">Loading…</div>
            ) : driverIntakes.length === 0 ? (
              <p className="text-gray-600 text-sm py-6">No driver submissions yet.</p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-3 font-medium">Submitted</th>
                      <th className="py-2 pr-3 font-medium">Name</th>
                      <th className="py-2 pr-3 font-medium">Contact</th>
                      <th className="py-2 pr-3 font-medium">Vehicle</th>
                      <th className="py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {driverIntakes.map((r) => (
                      <tr key={r.id} className="border-b border-gray-100 last:border-0">
                        <td className="py-2 pr-3 whitespace-nowrap text-gray-700">
                          {r.created_at
                            ? new Date(r.created_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="py-2 pr-3 font-medium text-gray-900">
                          {r.full_name}
                        </td>
                        <td className="py-2 pr-3 text-gray-700">
                          <div className="text-xs">{r.email}</div>
                          <div className="text-xs text-gray-500">{r.phone}</div>
                        </td>
                        <td className="py-2 pr-3 text-gray-700 max-w-[140px]">
                          <span className="line-clamp-2" title={`${r.vehicle_make} ${r.vehicle_model}`}>
                            {r.vehicle_make} {r.vehicle_model}
                          </span>
                          <div className="text-xs text-gray-500">{r.registration_plate}</div>
                        </td>
                        <td className="py-2 capitalize">{r.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex flex-row items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-teal-600" />
                  Support worker intakes
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Public support worker interest form — follow up by video before account setup.
                </p>
              </div>
              <Link
                to="/admin/applications"
                className="text-sm font-medium text-teal-600 hover:text-teal-800 shrink-0"
              >
                Full list →
              </Link>
            </div>
            {intakesLoading && supportIntakes.length === 0 ? (
              <div className="text-center py-8 text-gray-600 text-sm">Loading…</div>
            ) : supportIntakes.length === 0 ? (
              <p className="text-gray-600 text-sm py-6">No support worker submissions yet.</p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-3 font-medium">Submitted</th>
                      <th className="py-2 pr-3 font-medium">Name</th>
                      <th className="py-2 pr-3 font-medium">Contact</th>
                      <th className="py-2 pr-3 font-medium">Experience</th>
                      <th className="py-2 font-medium">DBS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supportIntakes.map((r) => (
                      <tr key={r.id} className="border-b border-gray-100 last:border-0">
                        <td className="py-2 pr-3 whitespace-nowrap text-gray-700">
                          {r.created_at
                            ? new Date(r.created_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="font-medium text-gray-900">{r.full_name}</div>
                          <div className="text-xs capitalize text-gray-500">{r.status}</div>
                        </td>
                        <td className="py-2 pr-3 text-gray-700">
                          <div className="text-xs">{r.email}</div>
                          <div className="text-xs text-gray-500">{r.phone}</div>
                        </td>
                        <td className="py-2 pr-3 text-gray-700 max-w-[180px]">
                          <span className="line-clamp-2" title={r.experience_summary}>
                            {r.experience_summary}
                          </span>
                        </td>
                        <td className="py-2 text-gray-700">
                          {r.has_current_dbs ? 'Declared' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Admin Actions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
              >
                {action.title === 'Newsletter Subscribers' ? (
                  <div>
                    <div className="flex items-center mb-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClasses(action.color)} group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="ml-4 flex-1">
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {action.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {action.description}
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Active Subscribers:</span>
                        <span className="font-semibold text-gray-900">{newsletterStats.active}</span>
                      </div>
                      <button
                        onClick={() => {
                          exportNewsletterSubscribers();
                          setTimeout(() => scrollToActionZone('.export-status, .download-status'), 100);
                        }}
                        className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium flex items-center justify-center"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export Subscriber List
                      </button>
                    </div>
                  </div>
                ) : action.title === 'Payment Analytics' ? (
                  <div>
                    <div className="flex items-center mb-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClasses(action.color)} group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="ml-4 flex-1">
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {action.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {action.description}
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Platform Fees:</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(paymentStats.platform_fees)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Driver Payouts:</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(paymentStats.driver_payouts)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Support Payouts:</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(paymentStats.support_worker_payouts)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link to={action.href} className="block">
                    <div className="flex items-center mb-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClasses(action.color)} group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="ml-4 flex-1">
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {action.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {action.description}
                    </p>
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {/* Live bookings & system status */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Live bookings</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Realtime sync from your database — new rows and updates appear without
                  refreshing. Optional desktop alerts; a future mobile app can use the
                  same events for push.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => requestAdminBookingNotifications()}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
                >
                  <Bell className="w-4 h-4" />
                  Enable alerts
                </button>
                <Link
                  to="/admin/bookings"
                  className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-800 hover:bg-gray-50"
                >
                  View all
                </Link>
              </div>
            </div>

            {liveError && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                Live feed: {liveError}. Check admin RLS and that{' '}
                <code className="text-xs">bookings</code> is in the Realtime publication.
              </p>
            )}

            {liveLoading && liveBookings.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading bookings…</p>
              </div>
            ) : liveBookings.length === 0 ? (
              <p className="text-gray-600 text-sm py-6 text-center">No bookings yet.</p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-3 font-medium">When</th>
                      <th className="py-2 pr-3 font-medium">Customer</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pr-3 font-medium">Payment</th>
                      <th className="py-2 font-medium">Route</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveBookings.map((row) => {
                      const r = row as Record<string, unknown>;
                      const id = String(r.id ?? '');
                      const ref = id.replace(/-/g, '').slice(0, 8).toUpperCase();
                      const pickup = String(r.pickup_address ?? '');
                      const drop = String(r.dropoff_address ?? '');
                      return (
                        <tr key={id || ref} className="border-b border-gray-100 last:border-0">
                          <td className="py-2 pr-3 whitespace-nowrap text-gray-700">
                            {r.created_at
                              ? new Date(String(r.created_at)).toLocaleString()
                              : '—'}
                          </td>
                          <td className="py-2 pr-3">
                            <div className="font-medium text-gray-900">
                              {String(r.customer_name ?? 'Guest')}
                            </div>
                            <div className="text-xs text-gray-500">{ref}</div>
                          </td>
                          <td className="py-2 pr-3 capitalize">
                            {String(r.status ?? '')}
                          </td>
                          <td className="py-2 pr-3 capitalize">
                            {String(r.payment_status ?? '')}
                          </td>
                          <td className="py-2 text-gray-700 max-w-[220px]">
                            <span className="line-clamp-2" title={`${pickup} → ${drop}`}>
                              {pickup.slice(0, 48)}
                              {pickup.length > 48 ? '…' : ''} →{' '}
                              {drop.slice(0, 32)}
                              {drop.length > 32 ? '…' : ''}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* System Status */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">System Status</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="font-medium text-gray-900">Database</span>
                </div>
                <span className="text-green-600 font-medium">Operational</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="font-medium text-gray-900">Payment System</span>
                </div>
                <span className="text-green-600 font-medium">Operational</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="font-medium text-gray-900">Email Service</span>
                </div>
                <span className="text-green-600 font-medium">Operational</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                  <span className="font-medium text-gray-900">Payment Processing</span>
                </div>
                <span className="text-blue-600 font-medium">Operational</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="font-medium text-gray-900">Stripe Connect</span>
                </div>
                <span className="text-green-600 font-medium">Active</span>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="mt-6 bg-gradient-to-r from-green-600 to-blue-600 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold mb-4">Payment Overview</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-green-100 text-sm">Platform Revenue</p>
                  <p className="text-xl font-bold">{formatCurrency(paymentStats.platform_fees)}</p>
                </div>
                <div>
                  <p className="text-green-100 text-sm">Total Processed</p>
                  <p className="text-xl font-bold">{formatCurrency(paymentStats.total_revenue)}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4">
              <Link
                to="/admin/users"
                className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg text-center transition-colors"
              >
                <UserPlus className="w-8 h-8 mx-auto mb-3" />
                <h4 className="font-semibold mb-2">Manage Users</h4>
                <p className="text-blue-100 text-sm">Upgrade user to admin role</p>
              </Link>

              <Link
                to="/admin/applications"
                className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg text-center transition-colors"
              >
                <FileText className="w-8 h-8 mx-auto mb-3" />
                <h4 className="font-semibold mb-2">Review Applications</h4>
                <p className="text-blue-100 text-sm">Approve pending applications</p>
              </Link>

              <Link
                to="/admin/vehicles"
                className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg text-center transition-colors"
              >
                <Car className="w-8 h-8 mx-auto mb-3" />
                <h4 className="font-semibold mb-2">Verify Vehicles</h4>
                <p className="text-blue-100 text-sm">Approve vehicle registrations</p>
              </Link>
            </div>
          </div>

          {/* Payment Analytics */}
          <div className="mt-8">
            <PaymentAnalytics />
          </div>
        </div>
      </div>
      
      {/* Performance Monitor */}
      <PerformanceMonitor />
    </>
  );
};

export default AdminDashboard;